/**
 * Completion cache — stores workspace IDs/names in ~/.arbi/completions.json.
 *
 * Populated as a side-effect of commands that list workspaces (login, register, workspaces).
 * Read synchronously during TAB completion for instant response.
 */

import fs from 'node:fs'
import path from 'node:path'
import os from 'node:os'

export interface CachedWorkspace {
  id: string
  name: string
}

interface CompletionCache {
  workspaces: CachedWorkspace[]
  updatedAt: string
}

function getCacheFile(): string {
  const configDir = process.env.ARBI_CONFIG_DIR ?? path.join(os.homedir(), '.arbi')
  return path.join(configDir, 'completions.json')
}

function ensureDir(filePath: string): void {
  const dir = path.dirname(filePath)
  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true, mode: 0o700 })
  }
}

/** Read cached workspace IDs (sync for TAB completion speed). */
export function getCachedWorkspaceIds(): string[] {
  try {
    const content = fs.readFileSync(getCacheFile(), 'utf-8')
    const cache = JSON.parse(content) as CompletionCache
    return cache.workspaces.map((w) => w.id)
  } catch {
    return []
  }
}

/**
 * Look up a workspace's name in the completion cache by ID. Used by
 * `arbi status` to render `Workspace: <Name> (wrk-…)` without an API
 * round-trip — `status` is offline by contract.
 */
export function getCachedWorkspaceName(id: string): string | null {
  try {
    const content = fs.readFileSync(getCacheFile(), 'utf-8')
    const cache = JSON.parse(content) as CompletionCache
    const hit = cache.workspaces.find((w) => w.id === id)
    return hit?.name?.trim() ? hit.name : null
  } catch {
    return null
  }
}

/** Update the completion cache with workspace data. */
export function updateCompletionCache(
  workspaces: Array<{ external_id?: string; name?: string }>
): void {
  const cache: CompletionCache = {
    workspaces: workspaces
      .filter((w) => w.external_id)
      .map((w) => ({ id: w.external_id!, name: w.name ?? '' })),
    updatedAt: new Date().toISOString(),
  }
  const filePath = getCacheFile()
  ensureDir(filePath)
  fs.writeFileSync(filePath, JSON.stringify(cache, null, 2) + '\n', { mode: 0o600 })
}
